import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useCallback, useEffect, useState, useRef } from "react";
import "./PostDetail.scss";
import PostContent from "./PostContent";
import PostActions from "../Components/PostActions/PostActions";
import PostMedia from "../Components/PostMedia/PostMedia";
import PollContent from "../PollContent/PollContent";
import PostHeader from "./PostHeader";
import { usePost } from "../../hooks/usePost";
import PostContentShare from "../Components/PostContentShare/PostContentShare";
import PostChallenge from "../Components/PostChallenge/PostChallenge";
import { useSelector } from "react-redux";
import PostComments from "./PostComments";
import SkeletonLoader from "../Components/SkeletonLoader/SkeletonLoader";
import AddPost from "../AddPost/AddPost";
import Toast from "../../components/Toast/Toast";
import { TbArrowBackUp } from "react-icons/tb";

const PostDetail = () => {
    const [editingPost, setEditingPost] = useState(null);
    const [showEditPopup, setShowEditPopup] = useState(false);
    const [toast, setToast] = useState({ show: false, message: "", type: "success" });
    const [postId, setPostId] = useState(null);
    const [isInitialLoading, setIsInitialLoading] = useState(false);
    const [postNotFound, setPostNotFound] = useState(false);
    const isDeletedRef = useRef(false);
    const hasFetchedRef = useRef(false);
    const { community_block_comment_add, uid } = useSelector((state) => state.projectInfo);

    const currentUser = uid;
    const categories = useSelector((state) => state.categories.entities) || [];
    const userPosts = useSelector((state) => state.posts.userPosts) || {};
    const { vanityUrl } = useParams();
    const { fetchCategories } = usePost();

    const {
        posts,
        singlePostLoading,
        getPostById,
        likePost,
        togglePostComments,
        voteOnPoll,
        setPostMediaIndex,
        activeMediaIndices = {},
        updatePost,
        deletePost
    } = usePost();

    const navigate = useNavigate();
    const location = useLocation();
    const { userId, post_id } = location.state || {};

    const handleGoBack = useCallback(async() => {
        await getPostById(vanityUrl);
        window.history.back();
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
        const container = document.querySelector(".community-content");
        if (container) {
            container.scrollTop = 0;
        }
    }, []);

    /**
     * Fetch post details when vanityUrl or location changes
     * Resets hasFetchedRef to allow re-fetching on location change
     */
    useEffect(() => {
        hasFetchedRef.current = false;
    }, [location, vanityUrl]);

    useEffect(() => {
        if (isDeletedRef.current || !vanityUrl || !currentUser || hasFetchedRef.current) return;
        const fetchPostDetails = async () => {
            hasFetchedRef.current = true;

            if (post_id) {
                if (posts[post_id]) {
                    setPostId(post_id);
                    
                    // Fetch categories in background if needed
                    if (!categories || categories.length === 0) {
                        fetchCategories().catch(err => console.error("Error fetching categories:", err));
                    }
                    return;
                }
                
                if (userId && userPosts[userId]?.[post_id]) {
                    setPostId(post_id);
                    
                    if (!categories || categories.length === 0) {
                        fetchCategories().catch(err => console.error("Error fetching categories:", err));
                    }
                    return;
                }
            }

            setIsInitialLoading(true);
            setPostNotFound(false);

            try {
                const response = await getPostById(vanityUrl);
                
                if (response?.payload?.id) {
                    const fetchedPostId = response.payload.id;
                    setPostId(fetchedPostId);
                    
                    if (!categories || categories.length === 0) {
                        await fetchCategories();
                    }
                } else {
                    setPostNotFound(true);
                }
            } catch (error) {
                console.error("Error fetching post details:", error);
                setPostNotFound(true);
            } finally {
                setIsInitialLoading(false);
            }
        };

        fetchPostDetails();
    }, [vanityUrl, currentUser, getPostById, fetchCategories, categories, location]);

    const closeEditPopup = () => {
        setShowEditPopup(false);
        setEditingPost(null);
    };

    const handleEditPost = (post, postId) => {
        setEditingPost({ ...post, postId });
        setShowEditPopup(true);
    };

    const hideToast = useCallback(() => {
        setToast(prev => ({ ...prev, show: false }));
    }, []);

    const showToast = useCallback((message, type = "success") => {
        setToast({ show: true, message, type });
    }, []);

    const handleEditSave = async (postId, updatedPostData) => {
        try {
            await updatePost(postId, updatedPostData);
            showToast("Post updated successfully");
            closeEditPopup();
        } catch (error) {
            console.error("Error updating post:", error);
            showToast("Failed to update post", "error");
        }
    };

    const handleDeletePost = async (postId) => {
        try {
            await deletePost(postId);
            showToast("Post deleted successfully");
            isDeletedRef.current = true;
            navigate("/community");
        } catch (error) {
            console.error("Error deleting post:", error);
            showToast("Failed to delete post", "error");
        }
    };

    const onDisableComment = useCallback(async () => {
        if (!postId) return;
        const currentStatus = posts[postId]?.comments_disabled;
        await togglePostComments(postId, currentStatus);
    }, [posts, postId, togglePostComments]);

    const onCarousalButtonClick = (postId, mediaKeys, direction) => {
        const activeIndex = activeMediaIndices[postId] || 0;
        const newIndex = direction === "prev"
            ? Math.max(0, activeIndex - 1)
            : Math.min(mediaKeys?.length - 1, activeIndex + 1);
        setPostMediaIndex(postId, newIndex);
    };

    const handlePollVote = (postid, updatedOptions) => {
        voteOnPoll(postid, updatedOptions);
    };

    const handleToggleLike = async (postId, isLiked) => {
        await likePost(postId, isLiked);
    };

    let post = undefined;
    
    if (postId) {
        if (userId && userPosts[userId]?.[postId]) {
            post = userPosts[userId][postId];
        } 
        else if (posts[postId]) {
            post = posts[postId];
        }
    }

    // Only show loader if we're loading AND don't have post data
    const isLoading = isInitialLoading && !post;

    return (
        <div className="instagram-container">
            <div className="back-button-container">
                <button onClick={handleGoBack} className="back-button">
                    <TbArrowBackUp />
                    <span>Back</span>
                </button>
            </div>

            {isLoading ? (
                <SkeletonLoader count={1} />
            ) : !post && postNotFound ? (
                <div className="post-not-found">
                    <div className="post-not-found-content">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="64"
                            height="64"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="12" cy="12" r="10"></circle>
                            <path d="M12 8v4"></path>
                            <path d="M12 16h0"></path>
                        </svg>
                        <h2>Post Not Found</h2>
                        <p>The post you're looking for may have been removed or doesn't exist.</p>
                        <button
                            onClick={() => navigate('/community')}
                            className="return-button"
                        >
                            Return to Feed
                        </button>
                    </div>
                </div>
            ) : post ? (
                <div key={postId} className="post-card">
                    <PostHeader
                        post={post}
                        onCommentDisable={onDisableComment}
                        postId={postId}
                        categories={categories}
                        onEditPost={handleEditPost}
                        onDeletePost={handleDeletePost}
                    />
                    {post.type === "CONTENT" && (
                        <PostContentShare content={post?.data} type={post?.content_type} />
                    )}
                    {post.media && Object.keys(post.media)?.length > 0 && (
                        <PostMedia
                            post={post}
                            postId={postId}
                            activeIndex={activeMediaIndices[postId] || 0}
                            onCarousalButtonClick={onCarousalButtonClick}
                        />
                    )}
                    {post.type === "CHALLENGE" && (
                        <PostChallenge post={post} />
                    )}
                    {post.type === "POLL" && (
                        <PollContent
                            poll={post.data}
                            postId={postId}
                            currentUser={currentUser}
                            onVote={handlePollVote}
                        />
                    )}
                    <div className="post-caption">
                        <PostContent content={post?.content} gif={post.gif_url} />
                    </div>
                    <PostActions
                        comments={Number(post?.comment_count) || 0}
                        isLiked={post?.user_has_liked}
                        postId={postId}
                        toggleLikePost={handleToggleLike}
                        isCommentsDisabled={post.comments_disabled}
                        likesCount={Number(post?.like_count) || 0}
                    />
                    {!post.comments_disabled && (
                        <PostComments
                            postId={postId}
                            post={post}
                            isCommentingBlocked={community_block_comment_add || post?.commentingblocked}
                        />
                    )}
                </div>
            ) : null}

            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={hideToast}
                />
            )}
            {showEditPopup && (
                <AddPost
                    onClose={closeEditPopup}
                    isEditing={true}
                    editingPost={editingPost}
                    onEditSave={handleEditSave}
                />
            )}
        </div>
    );
};

export default PostDetail;